import React, { memo, useCallback } from 'react';

import PropTypes from 'prop-types';
import { InputControlLabel } from '@picsio/ui';

import DetailsBlock from '../DetailsBlock';
import PricingField from './PricingField';
import Tooltip from '../Tooltip/Tooltip';
import ReadOnlyFieldTooltip from './helpers/ReadOnlyFieldTooltip';
import s from './styles.module.scss';

const Pricing = ({
  price,
  compareAtPrice,
  taxable,
  unitCost,
  diff,
  detailsPanelVisibility,
  toggleVisibility,
  blockName,
  blockTitle,
  onChangePrice,
  onChangeCompareAtPrice,
  onChangeTaxable,
  onChangeUnitCost,
  hidePriceFields,
  hideUnitCost,
  readOnly,
  readOnlyTooltip,
}) => {
  const saveTaxable = useCallback(() => {
    if (readOnly) return;

    onChangeTaxable(!taxable);
  }, [taxable, onChangeTaxable, readOnly]);

  return (
    <DetailsBlock
      dataQa="productPricing"
      detailsPanelVisibility={detailsPanelVisibility}
      toggleVisibility={toggleVisibility}
      blockName={blockName}
      blockTitle={blockTitle}
    >
      <If condition={!hidePriceFields}>
        <div className={s.row}>
          <PricingField
            value={price}
            label={<Tooltip content={diff?.price ? 'Unpublished changes' : null} placement="top"><span className={diff?.price ? s.diffDot : undefined}>Price</span></Tooltip>}
            readOnly={readOnly}
            readOnlyTooltip={readOnlyTooltip}
            onCommit={onChangePrice}
          />
          <PricingField
            value={compareAtPrice}
            label={<Tooltip content={diff?.compareAtPrice ? 'Unpublished changes' : null} placement="top"><span className={diff?.compareAtPrice ? s.diffDot : undefined}>Compare-at price</span></Tooltip>}
            readOnly={readOnly}
            readOnlyTooltip={readOnlyTooltip}
            clearable
            onCommit={onChangeCompareAtPrice}
          />
        </div>
      </If>
      <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
        <div className={s.row}>
          <InputControlLabel
            control="checkbox"
            label={<Tooltip content={diff?.taxable ? 'Unpublished changes' : null} placement="top"><span className={diff?.taxable ? s.diffDot : undefined}>Charge tax on this product</span></Tooltip>}
            value={taxable}
            disabled={readOnly}
            onChange={saveTaxable}
          />
        </div>
      </ReadOnlyFieldTooltip>
      <If condition={!hideUnitCost}>
        <hr className={s.hr} />
        <div className={s.row}>
          <PricingField
            value={unitCost?.amount ?? ''}
            label={<Tooltip content={diff?.unitCost ? 'Unpublished changes' : null} placement="top"><span className={diff?.unitCost ? s.diffDot : undefined}>Cost per item</span></Tooltip>}
            readOnly={readOnly}
            readOnlyTooltip={readOnlyTooltip}
            clearable
            onCommit={onChangeUnitCost}
          />
        </div>
      </If>
    </DetailsBlock>
  );
};

Pricing.propTypes = {
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  compareAtPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  taxable: PropTypes.bool,
  unitCost: PropTypes.shape({ amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  diff: PropTypes.shape({
    price: PropTypes.bool,
    compareAtPrice: PropTypes.bool,
    taxable: PropTypes.bool,
    unitCost: PropTypes.bool,
  }),
  detailsPanelVisibility: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleVisibility: PropTypes.func.isRequired,
  blockName: PropTypes.string.isRequired,
  blockTitle: PropTypes.node.isRequired,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  onChangePrice: PropTypes.func.isRequired,
  onChangeCompareAtPrice: PropTypes.func.isRequired,
  onChangeTaxable: PropTypes.func.isRequired,
  onChangeUnitCost: PropTypes.func.isRequired,
  hidePriceFields: PropTypes.bool,
  hideUnitCost: PropTypes.bool,
};

Pricing.defaultProps = {
  price: '',
  compareAtPrice: '',
  taxable: false,
  unitCost: null,
  diff: {},
  readOnly: false,
  readOnlyTooltip: null,
  hidePriceFields: false,
  hideUnitCost: false,
};

export default memo(Pricing);
